import { Injectable } from '@angular/core';

export enum targetType {
  byTagName,
  byId
}

export enum srcType {
  src,
  innerText,
  innerHTML
}

export interface ScriptModel {
  name: string;
  target: targetType;
  targetName: string;
  srcTarget: srcType;
  src: string;
  loaded: boolean;
}

/**
 * Service that allows dynamically loading an external script into the page at runtime.
 */
@Injectable()
export class ScriptLoaderService {
  private scripts: ScriptModel[] = [];

  /**
   * Load the given script dynamically.
   * @param script {ScriptModel | string} Script to load. The script may either be an instance of ScriptModel,
   *  or a URL.
   * @param [reload] {boolean} If true, force the script to be reloaded even if it was previously loaded.
   * @returns {Promise<ScriptModel>}
   */
  public load(script: ScriptModel|string, reload: boolean = false): Promise<ScriptModel> {
    return new Promise<ScriptModel>((resolve, reject) => {
      // Create a ScriptModel instance on the fly if the passed script is a URL
      const scriptModel: ScriptModel = typeof script === 'string' ? {
        name: script,
        target: targetType.byTagName,
        targetName: 'body',
        srcTarget: srcType.src,
        src: script,
        loaded: false
      } : script;

      // Check if script was previously loaded
      const existingScript = this.scripts.find(s => s.name === scriptModel.name);

      // Complete if already loaded (unless we're doing a forced reload)
      if (existingScript && existingScript.loaded && !reload) {
        resolve(existingScript);
        return;
      } else {
        // Add the script
        this.scripts = [...this.scripts, scriptModel];

        // Load the script
        const scriptElement = document.createElement('script');
        scriptElement.type = 'text/javascript';

        // Add the script to the appropriate location in the script element
        if (scriptModel.srcTarget === srcType.src) {
          scriptElement.src = scriptModel.src;
        } else if (scriptModel.srcTarget === srcType.innerText) {
          scriptElement.innerText = scriptModel.src;
        } else {
          scriptElement.innerHTML = scriptModel.src;
        }

        scriptElement.onload = () => {
          scriptModel.loaded = true;
          resolve(scriptModel);
        };

        scriptElement.onerror = (error: any) => {
          reject('Couldn\'t load script ' + scriptModel.src);
        };

        // Send the script to the desired spot in the html.
        if (scriptModel.target === targetType.byTagName) {
          document.getElementsByTagName(scriptModel.targetName)[0].appendChild(scriptElement);
        } else {
          document.getElementById(scriptModel.targetName).appendChild(scriptElement);
        }
      }
    });
  }
}
